// src/pages/PricingOptionsPage.jsx
import React, { useState } from 'react';
import { useLocation, Link, useSearchParams, useNavigate } from 'react-router-dom'; 
import { PRICING_DATA, SPECIAL_PLANS_DATA, findCourseData } from '../data/pricingData';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { auth } from '../firebase'; 

const functions = getFunctions(getApp()); 
const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

// --- Sub-component for a single course's pricing options ---
const CoursePricingCard = ({ course }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams(); 
    const navigate = useNavigate(); 
    
    // Calculate Annual Prices (20% Discount off the Per-Lesson Total)
    // Formula: (Price Per Lesson * Total Lessons) * 0.80
    const oneOnOneAnnual = (course.price * course.lessons * 0.8).toFixed(2); 
    const groupAnnual = (course.groupPrice * course.lessons * 0.8).toFixed(2);

    const handleStripeCheckout = async (planKey) => {
        setIsLoading(true);
        setError('');

        const user = auth.currentUser;
        if (!user) {
            alert('Please sign in or create an account to enroll.');
            navigate('/login');
            setIsLoading(false);
            return;
        }

        const referrerId = searchParams.get('ref') || localStorage.getItem('aulice_ref_id');
        const priceId = course.stripePriceIds ? course.stripePriceIds[planKey] : null;
        const courseId = course.id; 

        if (!priceId) {
            setError(`Stripe Price ID for plan "${planKey}" not found. Please contact support.`);
            setIsLoading(false);
            return;
        }

        try {
            const result = await createCheckoutSession({
                priceId: priceId,
                courseId: courseId,
                refId: referrerId, 
                plan: planKey 
            });

            if (result.data && result.data.sessionUrl) {
                window.location.href = result.data.sessionUrl;
            } else {
                throw new Error(result.data?.error || 'No checkout URL returned.');
            }
        } catch (err) {
            console.error("Stripe checkout error:", err);
            if (err?.message?.includes('No product found')) {
                 setError("Error: This plan is not fully configured in Stripe yet.");
            } else if (err?.code === 'unauthenticated') {
                 setError("Please log in to continue.");
            } else {
                setError(`Checkout failed: ${err?.message || 'Unknown error'}.`);
            }
            setIsLoading(false);
        }
    };
    
    return (
        <section className="bg-white rounded-2xl shadow-xl p-8 mb-12 border border-gray-100">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">{course.name}</h2>
            <p className="text-center text-gray-500 mb-8 italic">{course.description}</p>
            
            {error && <p className="text-red-500 text-center mb-4 bg-red-50 p-3 rounded">{error}</p>}

            <div className="flex flex-col lg:flex-row justify-center items-stretch gap-8">
                {/* 1:1 Pricing Column */}
                <div className="flex-1 bg-white border border-blue-100 rounded-2xl p-6 text-center flex flex-col relative shadow-sm hover:shadow-md transition">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide">
                        Premium Choice
                    </div>
                    <div className="mt-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">1:1 Coaching</h3>
                        <p className="text-gray-500 mb-6">Dedicated personalized sessions</p>
                        
                        <div className="text-4xl font-extrabold text-gray-900 mb-2">
                            ${course.price}<span className="text-lg font-medium text-gray-400">/lesson</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">or <span className="font-bold text-gray-700">${course.monthly}/month</span></p>

                        <ul className="text-left text-sm text-gray-600 space-y-3 mb-8 bg-gray-50 p-4 rounded-lg">
                            <li className="flex items-center">✅ Total of {course.lessons} lessons</li>
                            <li className="flex items-center">✅ {course.lessonsPerMonth} lessons per month</li>
                            <li className="flex items-center">✅ {course.schedule}</li>
                            <li className="flex items-center">✅ Full course access</li>
                        </ul>
                    </div>
                    <div className="mt-auto space-y-3">
                        <button disabled={isLoading} onClick={() => handleStripeCheckout('one_one_monthly')} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition disabled:bg-gray-300">
                             {isLoading ? 'Processing...' : `Start Monthly: $${course.monthly}`}
                        </button>
                    </div>
                </div>

                {/* Group Pricing Column */}
                <div className="flex-1 bg-white border border-gray-200 rounded-2xl p-6 text-center flex flex-col shadow-sm hover:shadow-md transition">
                    <div className="mt-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Group Mode</h3>
                        <p className="text-gray-500 mb-6">Collaborate with peers</p>
                        
                        <div className="text-4xl font-extrabold text-gray-900 mb-2">
                            ${course.groupPrice}<span className="text-lg font-medium text-gray-400">/lesson</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">or <span className="font-bold text-gray-700">${course.groupMonthly}/month</span></p>

                        <ul className="text-left text-sm text-gray-600 space-y-3 mb-8 bg-gray-50 p-4 rounded-lg">
                            <li className="flex items-center">👥 Total of {course.lessons} lessons</li>
                            <li className="flex items-center">👥 {course.lessonsPerMonth} lessons per month</li>
                            <li className="flex items-center">🗓️ {course.groupSchedule || 'Fixed schedule'}</li>
                            <li className="flex items-center">✅ Full course access</li>
                        </ul>
                    </div>
                    <div className="mt-auto space-y-3">
                        <button disabled={isLoading} onClick={() => handleStripeCheckout('group_monthly')} className="w-full bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-bold py-3 px-6 rounded-lg transition disabled:bg-gray-100">
                             {isLoading ? 'Processing...' : `Start Monthly: $${course.groupMonthly}`}
                        </button>
                    </div>
                </div>
            </div>

            {/* Annual Plan Section */}
            <div className="mt-10 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-8 text-center">
                <h4 className="text-2xl font-bold text-indigo-900 mb-2">Annual Plan: Save 20%</h4>
                <p className="text-indigo-600 mb-6">Commit to the full mastery journey and get two months free.</p>
                
                <div className="flex flex-col md:flex-row justify-center gap-6">
                    <button onClick={() => handleStripeCheckout('one_one_annual')} className="flex-1 bg-indigo-700 hover:bg-indigo-800 text-white font-bold py-4 px-6 rounded-lg shadow-md transition">
                        1:1 Annual Access: <span className="text-yellow-300 ml-1">${oneOnOneAnnual}</span>
                    </button>
                    <button onClick={() => handleStripeCheckout('group_annual')} className="flex-1 bg-white border-2 border-indigo-700 text-indigo-700 hover:bg-indigo-50 font-bold py-4 px-6 rounded-lg shadow-sm transition">
                        Group Annual Access: <span className="ml-1">${groupAnnual}</span>
                    </button>
                </div>
            </div>
        </section>
    );
};

// --- Special Plans Card ---
const SpecialPlanCard = ({ plan }) => {
     return (
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-12 border border-gray-100 flex flex-col items-center text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{plan.name}</h2>
            <p className="text-gray-600 mb-6 max-w-lg">{plan.description}</p>
            <div className="bg-green-50 text-green-800 font-bold px-6 py-2 rounded-full mb-6">
                {plan.priceRange}
            </div>
            {plan.href ? (
                <Link to={plan.href} className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-10 rounded-full shadow-lg transition transform hover:-translate-y-1">
                    {plan.cta}
                </Link>
            ) : (
                <button className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-10 rounded-full shadow-lg transition">
                    {plan.cta} 
                </button>
            )}
        </section>
    );
};

// --- Main Pricing Options Page ---
const PricingOptionsPage = () => {
    const location = useLocation();
    const courseIdFromState = location.state?.course?.id;
    const specificCourseData = courseIdFromState ? findCourseData(courseIdFromState) : null;

    return (
        <div className="bg-gray-50 min-h-screen">
            <header className="w-full text-center py-16 bg-blue-900 text-white shadow-xl">
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Pricing & Plans</h1>
                 <p className="text-xl mt-4 max-w-2xl mx-auto px-4 text-blue-100">
                     {specificCourseData
                        ? `Select your plan for: ${specificCourseData.name}`
                        : "Flexible options designed for your professional growth."
                    }
                </p>
            </header>

            <main className="w-full max-w-6xl mx-auto my-12 px-4">
                {specificCourseData ? (
                    <CoursePricingCard course={specificCourseData} />
                ) : (
                    <>
                        <h2 className="text-2xl font-bold text-gray-800 mb-10 pl-4 border-l-4 border-blue-600">Professional Tracks</h2>
                        {Object.values(PRICING_DATA).map(course => <CoursePricingCard key={course.id} course={course} />)}

                        <h2 className="text-2xl font-bold text-gray-800 mt-20 mb-10 pl-4 border-l-4 border-green-600">Specialized Programs</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            {SPECIAL_PLANS_DATA.map(plan => <SpecialPlanCard key={plan.id} plan={plan} />)}
                        </div>
                    </>
                )}

                 <div className="text-center mt-16 mb-8">
                    <Link to="/" className="text-gray-500 hover:text-blue-600 font-semibold transition">
                        &larr; Back to Course Catalog
                    </Link>
                </div>
            </main>
        </div>
    );
};

export default PricingOptionsPage;